import React, { FC } from 'react';
import styled from 'styled-components';
import { Link } from '../buttons';
import { HelpIcon, HelpIconContainer } from '../helpIconContainer';
import { CheckboxIndeterminate } from '../svg/checkbox-indeterminate';
import { CheckboxSelected } from '../svg/checkbox-selected';
import { ZTooltip } from '../ztooltip/ztooltip';

export type CheckboxVariant = 'default' | 'ai' | 'compact';

const CaptionContainer = styled.div`
    display: flex;
    margin-left: 25px;
`;

export const CheckboxContainer = styled.div<{ disabled?: boolean; $hasCaption?: boolean }>`
    margin-right: 10px;
    cursor: default;
    opacity: ${(props) => (props.disabled ? '0.6' : '1')};
    display: inline-flex;
    ${(props) =>
        props.$hasCaption &&
        `
        align-items: flex-start;
        flex-direction: column;
    `}
`;

const CheckboxInput = styled.input<{
    disabled?: boolean;
    checked?: boolean;
    $indeterminate?: boolean;
    $variant: CheckboxVariant;
}>`
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    width: 16px;
    height: 16px;
    appearance: none;
    border-radius: 2px;
    transition:
        background 0.2s ease,
        border-color 0.2s ease;

    background: ${(props) => {
        if (props.disabled) return props.theme.app.component.checkbox.selected.disabled.inputColor;
        if (props.checked) {
            return props.$variant === 'ai'
                ? 'linear-gradient(90deg, #3B82F6 3.75%, #8B5CF6 103.75%)'
                : props.theme.app.component.checkbox.selected.active.background;
        }
        return props.theme.app.component.checkbox.unselected.active.background;
    }};

    border: 1.75px solid
        ${(props) => {
            if (props.disabled) return props.theme.app.component.checkbox.selected.disabled.border;
            if (props.checked) {
                return props.$variant === 'ai'
                    ? 'linear-gradient(90deg, #3B82F6 3.75%, #8B5CF6 103.75%)'
                    : props.theme.app.component.checkbox.selected.active.border;
            }
            return props.theme.app.component.checkbox.unselected.active.border;
        }};

    opacity: ${(props) => (props.disabled ? '0.6' : '1')};
`;

const CheckboxWrapper = styled.div`
    position: relative;
    width: 24px;
    height: 24px;
    display: inline-block;
    cursor: pointer;
    outline: none;
`;

const CheckboxContent = styled.div<{ $checked?: boolean; $indeterminate?: boolean }>`
    width: 24px;
    height: 24px;
    position: absolute;
    top: 45%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: ${(props) => (props.$indeterminate || props.$checked ? 'block' : 'none')};
`;

const CheckboxLabelContainer = styled.div<{ $variant: CheckboxVariant }>`
    padding-top: 2px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    > label {
        margin-left: 5px;
        line-height: ${(props) => (props.$variant === 'compact' ? '16px' : '20px')};
        font-size: ${(props) => (props.$variant === 'compact' ? '12px' : '14px')};
        color: ${(props) => (props.$variant === 'compact' ? '#374151' : 'inherit')};
        white-space: pre-wrap;
        text-overflow: ellipsis;
        overflow: hidden;
        cursor: pointer;
    }
`;

const CheckboxCaption = styled.span`
    margin-left: 5px;
    display: inline-block;
    font-size: 12px;
    line-height: 16px;
    color: rgb(68, 90, 100);
`;

const StyledHelpIconContainer = styled(HelpIconContainer)`
    margin-left: 5px;
    vertical-align: middle;
`;

const ParentContainer = styled.div`
    display: inline-flex;
    flex-direction: row;
`;

const BadgeContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;

    > *:first-child {
        margin-left: 24px;
    }
`;

interface ReadMore {
    label: string;
    link: string;
}

export interface CheckboxProps {
    checked: boolean;
    indeterminate?: boolean;
    label?: React.ReactNode;
    caption?: React.ReactNode;
    className?: string;
    disabled?: boolean;
    disabledTooltip?: string | React.ReactNode;
    helpTooltip?: React.ReactNode | string | null;
    helpTooltipPosition?: 'left' | 'top' | 'right' | 'bottom';
    iconTooltip?: React.ReactNode;
    allowChangeOnDisabled?: boolean;
    readMore?: ReadMore;
    onChange: (isChecked: boolean) => void;
    onClick?: (e: React.MouseEvent) => void;
    variant?: CheckboxVariant;
    badges?: React.ReactNode[];
}

export const _Checkbox: FC<CheckboxProps> = (props) => {
    const handleChange = (isChecked: boolean) => {
        if (!props.disabled || props.allowChangeOnDisabled) props.onChange(isChecked);
    };

    const variant = props.variant ?? 'default';

    const showDisabledTooltip = props.disabled && props.disabledTooltip;

    const content = (
        <CheckboxContainer
            disabled={props.disabled}
            $hasCaption={!!props.caption}
            onClick={(e) => {
                e.stopPropagation();
                props.onClick?.(e);
            }}
        >
            <ParentContainer>
                <CheckboxWrapper>
                    <CheckboxInput
                        type='checkbox'
                        checked={props.checked}
                        disabled={props.disabled}
                        $indeterminate={props.indeterminate}
                        onChange={(e) => handleChange(e.target.checked)}
                        $variant={variant}
                    />
                    {props.checked && (
                        <CheckboxContent onClick={() => handleChange(!props.checked)} $checked={props.checked} $indeterminate={props.indeterminate}>
                            {props.indeterminate ? <CheckboxIndeterminate /> : <CheckboxSelected />}
                        </CheckboxContent>
                    )}
                </CheckboxWrapper>

                <CheckboxLabelContainer $variant={variant}>
                    {props.label && <label onClick={() => handleChange(!props.checked)}>{props.label}</label>}
                </CheckboxLabelContainer>

                {props.readMore && (
                    <Link target={'_blank'} css={'margin-left:3px'} href={props.readMore.link}>
                        {props.readMore.label}
                    </Link>
                )}

                {props.helpTooltip && (
                    <StyledHelpIconContainer tooltip={props.helpTooltip} position={props.helpTooltipPosition} interactive>
                        {props.iconTooltip || <HelpIcon />}
                    </StyledHelpIconContainer>
                )}

                {props.badges && props.badges.length > 0 && (
                    <BadgeContainer>
                        {props.badges.map((badge, index) => (
                            <React.Fragment key={index}>{badge}</React.Fragment>
                        ))}
                    </BadgeContainer>
                )}
            </ParentContainer>

            {props.caption && (
                <CaptionContainer>
                    <CheckboxCaption>{props.caption}</CheckboxCaption>
                </CaptionContainer>
            )}
        </CheckboxContainer>
    );

    if (showDisabledTooltip) {
        return (
            <ZTooltip addContainer html={props.disabledTooltip}>
                {content}
            </ZTooltip>
        );
    }

    return content;
};

export const Checkbox = styled(_Checkbox)``;
